import React, { useEffect, useMemo, useState } from 'react';
import {useLocation} from 'react-router-dom';
import {Traffic} from '../aggregate-customer-traffic-config';
import TrafficTimelineChart from './traffic-timeline-chart/traffic-timeline-chart';
import reportService from '../../../../../services/report.service';
import {observer} from 'mobx-react-lite';
import {useStores} from '../../../../store';
import { Direction, DisableReasons, ReportName, VersionStatus } from '../../../../../utils/enums';
import DynamicList from '../../../../shared/dynamic-list/dynamic-list';
import {ReportDataItem} from '../../../../../dtos/reports-data-request';
import {HistogramDataResponse} from '../../../../../dtos/reports-data-response';
import {Bar} from './bar/bar';
import './network-traffic.scss';
import { useNetworkTrafficFiltersConfig } from '../../../../shared/filters/filter-config/network-traffic-filters';
import {colors} from '../../../../../utils/colors';
import {FwVersion, FwVersionContent} from '../../../../../dtos/version.dto';
import {RuleHelper} from '../../../../../utils/rule-helper';
import AllowedIcon from '@svg/v-circle-outline-small.svg?react';
import BlockedIcon from '@svg/block-circle.svg?react';
import CustomButton from '../../../../shared/custom-button/custom-button';
import usePromiseFeedback from '../../../../shared/promise-feedback/usePromiseFeedback';
import {
    DependenciesWarningModal,
    DependenciesWarningModalProps
} from '../../../apps-services/warnings/dependencies-warning-modal';
import {isVersionActive} from '../../../../../utils/versionUtils';
import {toastSuccessUpdateManageApp} from 'src/components/features/apps-services/apps-utils';
import {TooltipDisableReason} from '../../../../shared/tooltip-disable-message/tooltip-disable-reason';
import { getCurrentFilterLabel } from '@src/utils/filters-util';

interface AppItem {
    application: string;
    applicationData: { applicationMetadata: any, dependencies: [] };
    bytes: number;
    bytes_received: number;
    bytes_sent: number;
    direction: string;
}

const ALL_APPS = 'All Applications';
const ALL_AI_APPS = 'All AI apps';
const localStorageKey = 'networkTrafficFilter';
const UNIDENTIFIED_APPS = new Set(['insufficient-data', 'incomplete', 'not-applicable', 'unknown-p2p', 'unknown-tcp', 'unknown-udp']);

export const NetworkTraffic: React.FC<Traffic> = observer(({ data }: Traffic) => {
    const { state } = useLocation();
    const [direction, setDirection] = useState<Direction>(state?.direction ?? Direction.Inbound);
    const [chartData, setChartData] = useState<HistogramDataResponse>([]);
    const [values, setValues] = useState({ max: 0, total: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [dataByDirection, setDataByDirection] = useState<null | any[]>(null);
    const [version, setVersion] = useState<FwVersion | undefined>();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [dependenciesWarningModalData, setDependenciesWarningModalData] = useState<DependenciesWarningModalProps | null>(null);
    const [performingAction, setPerformingAction] = useState<string[]>([]);
    const { customerStore, fwVersionStore, reportsStore, userStore } = useStores();

    const networkTrafficFiltersConfig = useNetworkTrafficFiltersConfig();

    const getFilterLabel = () => {
        return getCurrentFilterLabel(localStorageKey, networkTrafficFiltersConfig.radioButtons?.options || []);
    };

    const [filterLabel, setFilterLabel] = useState<string>(() => {
        localStorage.setItem(localStorageKey, state?.aiDefense && customerStore.isPremium ? '1' : '0');
        return getCurrentFilterLabel(localStorageKey, networkTrafficFiltersConfig.radioButtons?.options || []);
    });
    const isAiDefenseFilter = useMemo(() => filterLabel === 'AI Defense', [filterLabel]);
    const [appName, setAppName] = useState(isAiDefenseFilter ? ALL_AI_APPS : ALL_APPS);

    const tabs: Direction[] = [Direction.Inbound, Direction.Outbound];

    useEffect(() => {
        const onStorageChange = (e: StorageEvent) => {
            if (e.key === localStorageKey) {
                setFilterLabel(getFilterLabel());
            }
        };
        window.addEventListener('storage', onStorageChange);
        return () => window.removeEventListener('storage', onStorageChange);
    }, []);

    const loadVersionData = async () => {
        const versionId = fwVersionStore.fwVersions?.[0]?.externalId;
        if (!versionId) return;
        const version = await fwVersionStore.getVersionData(versionId);
        setVersion(version);
    };

    useEffect(() => {
        localStorage.setItem(localStorageKey, state?.aiDefense && customerStore.isPremium ? '1' : '0');
        setFilterLabel(getFilterLabel());
        setDirection(state?.direction ?? Direction.Inbound);
    }, [state]);

    useEffect(() => {
        void loadVersionData();
    }, [fwVersionStore.fwVersions]);

    const allowedInboundAppsRule = useMemo(() => {
        if (!version) return undefined;
        return RuleHelper.getInternetToMyNetworkAllowedApplications(version);
    }, [version]);

    const allowedOutboundAppsRule = useMemo(() => {
        if (!version) return undefined;
        return RuleHelper.getMyNetworkToInternetAllowedApplications(version);
    }, [version]);

    const blockedInboundAppsRule = useMemo(() => {
        if (!version) return undefined;
        return RuleHelper.getInternetToMyNetworkBlockedApplications(version);
    }, [version]);

    const blockedOutboundAppsRule = useMemo(() => {
        if (!version) return undefined;
        return RuleHelper.getMyNetworkToInternetBlockedApplications(version);
    }, [version]);

    const managedApps = useMemo(() => {
        if (version && allowedInboundAppsRule && allowedOutboundAppsRule) {
            return RuleHelper.getManagedApps(version, allowedInboundAppsRule, allowedOutboundAppsRule, blockedInboundAppsRule, blockedOutboundAppsRule);
        }
        return [];
    }, [version, allowedInboundAppsRule, allowedOutboundAppsRule]);

    const convertToDataWithStatus = (data: any[]) => {
        return data.map(item => {
            const managedApp = managedApps.find(app => app.id === item.application);
            const itemWithStatus: any = { ...item };
            if (managedApp) {
                const directionProperty = direction === Direction.Inbound ? 'inboundAllowed' : 'outboundAllowed';
                itemWithStatus.status = managedApp[directionProperty] ? 'allowed' : 'blocked';
            }
            return itemWithStatus;
        });
    };

    useEffect(() => {
        setAppName(isAiDefenseFilter ? ALL_AI_APPS : ALL_APPS);
        const lowerCaseDirection = direction.toLowerCase();
        const relevantData = data?.filter(item =>
            item.direction === lowerCaseDirection && (isAiDefenseFilter ? item.aiDefense : true)) || [];
        const convertedData = convertToDataWithStatus(relevantData);
        setDataByDirection(convertedData);
    }, [data, direction, filterLabel]);

    useEffect(() => {
        if (dataByDirection) {
            getChartData();
        }
    }, [appName, dataByDirection]);

    const appStatusMap: { [key: string]: { inbound: boolean, outbound: boolean } } = useMemo(() => {
        return managedApps.reduce((acc, app) => {
            acc[app.id] = { inbound: app.inboundAllowed, outbound: app.outboundAllowed };
            return acc;
        }, {} as { [key: string]: { inbound: boolean, outbound: boolean } });
    }, [data, managedApps]);

    const getChartData = () => {
        setIsLoading(true);
        setNumbers();

        const request = reportsStore.createBodyRequest(ReportName.NETWORK_TRAFFIC);

        const histogramReportName = isAiDefenseFilter
            ? ReportName.AI_APPLICATION_HISTOGRAM
            : ReportName.APPLICATION_HISTOGRAM;

        const payload: ReportDataItem = {
            reportName: histogramReportName,
            startTime: request.startTime,
            endTime: request.endTime,
            extraFilters: {direction: direction.toLowerCase() as 'inbound' | 'outbound'},
        };

        if (appName !== ALL_APPS && appName !== ALL_AI_APPS) {
            payload.searchTerm = appName;
        }

        void reportService.getHistogramReportData(customerStore.customerId, customerStore.serviceId, payload).then(res => {
            setChartData(res);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const setNumbers = () => {
        if (dataByDirection?.length) {
            const maxBytes = Math.max(...dataByDirection.slice(1).map(item => item.bytes ?? 0), 0);
            setValues({ max: maxBytes, total: dataByDirection[0].bytes || 0 });
        }
    };

    const createDraftIfNeededAndUpdateStore = async () => {
        if (!version) return;
        if (isVersionActive(version)) {
            await fwVersionStore.createDraftVersion(version.externalId, customerStore.serviceId);
        }
        else {
            fwVersionStore.updateSelectedVersionInStore(version);
        }
    };

    function updateApp(app: AppItem, isAllow: boolean) {
        const actionMethod = isAllow ? RuleHelper.allowApp : RuleHelper.blockApp;

        const accessor = direction === Direction.Inbound ? 'inboundAllowed' : 'outboundAllowed';

        const appData = {
            ...app.applicationData.applicationMetadata,
            dependencies: app.applicationData.dependencies
        };

        actionMethod(appData,
            managedApps,
            () => update(app.application, isAllow),
            accessor,
            setDependenciesWarningModalData
        );
    }

    const update = async (appId: string, isAllowed: boolean) => {
        setPerformingAction([...performingAction, appId]);
        setDependenciesWarningModalData(null);
        await createDraftIfNeededAndUpdateStore();
        promiseFeedbackFactory({
            promise: fwVersionStore.changeAppStatus(appId, direction, isAllowed),
            successCallback: (res: FwVersionContent | undefined) => {
                if (!res) return;
                if (!fwVersionStore.selectedFwVersion) return;
                if (res.managed_apps.length !== fwVersionStore.selectedFwVersion.content.managed_apps.length)
                    toastSuccessUpdateManageApp();
                setVersion({ ...fwVersionStore.selectedFwVersion, content: res });
                setPerformingAction([...performingAction.filter(_ => _ !== appId)]);
            },
            errorCallback: () => {
                setPerformingAction([...performingAction.filter(_ => _ !== appId)]);
            },
            notifObj: { show: false }
        });
    };

    const getAction = (item: AppItem, direction: Direction) => {
        const appStatus = appStatusMap[item.application];
        const isDefaultAllowed = direction === Direction.Inbound ?
            RuleHelper.isAllowedByDefault(allowedInboundAppsRule) :
            RuleHelper.isAllowedByDefault(allowedOutboundAppsRule);
        return ((appStatus as any)?.[direction.toLowerCase()] || !appStatus && isDefaultAllowed) ? 'Block' : 'Allow';
    };

    const isPending = useMemo(() =>
        version && [VersionStatus.PUSH_SENT, VersionStatus.COMMIT_SENT].includes(version?.versionStatus),
        [version]);

    const ActionButton = ({ item, direction }: { item: AppItem, direction: Direction }) => {
        const action = getAction(item, direction);
        return <TooltipDisableReason disableReason={isPending ? DisableReasons.PENDING_EXIST : undefined}>
            <CustomButton
                className={`action-btn ${action === 'Allow' ? 'green' : 'red'} 
                secondary-btn ${performingAction.includes(item.application) ? 'loading' : ''}`}
                label={action}
                isDisabled={userStore.isReadOnlyUser || isPending}
                onClick={(e) => {
                    e.stopPropagation();
                    if (performingAction.includes(item.application)) return;
                    updateApp(item, action === 'Allow');
                }}
            />
        </TooltipDisableReason>;
    };

    const StatusIcon = ({ item }: { item: any }) => {
        const appStatus = appStatusMap[item.application];
        const status = (appStatus as any)?.[direction.toLowerCase()];
        return (
            <span className={status ? 'allowed' : 'blocked'}>
                {status ? <AllowedIcon /> : <BlockedIcon />}
            </span>
        );
    };

    const displayAppName = useMemo(() => {
        const app = dataByDirection?.find(_ => _.application === appName);
        return app?.applicationData?.applicationMetadata?.displayName || appName;
    }, [appName, dataByDirection]);

    return (
        <div className="network-traffic-wrapper">
            <div className="network-traffic-header">
                <div className="network-traffic-tabs">
                    {tabs.map(tab => (
                        <button type="button" key={tab}
                            onClick={() => setDirection(tab)}
                            className={`tab ${direction === tab ? 'active' : ''}`}>
                            {tab}
                        </button>
                    ))}
                </div>
                <div className="app-name">{displayAppName}</div>
            </div>

            <TrafficTimelineChart data={chartData} isInbound={direction === Direction.Inbound} isLoading={isLoading} />

            <div className="network-traffic-list-wrapper">
                {data && (!customerStore.isPremium || filterLabel) &&
                    <DynamicList
                        list={dataByDirection || []}
                        title="Apps"
                        filterConfig={networkTrafficFiltersConfig}
                        fields={[
                            {
                                key: 'application',
                                display: (item) => <div className="app-name col">
                                    {item.applicationData?.applicationMetadata?.displayName || item.application}
                                </div>
                            },
                            {
                                key: 'number', display: (item) =>
                                    <div className="percent col">
                                        <Bar
                                            value={item.bytes}
                                            total={values.total}
                                            max={values.max}
                                            bgColor={direction === Direction.Inbound ? colors.secondaryBlue : colors.primaryBlue}
                                            isBarDisplay={item.application !== ALL_APPS && item.application !== ALL_AI_APPS}
                                        />
                                    </div>
                            },
                            {
                                key: 'status',
                                display: (item) => <div className="status-icon col">
                                    {appStatusMap[item.application] &&
                                        <StatusIcon item={item} />}
                                </div>
                            },
                            {
                                key: 'action',
                                display: (item: AppItem) =>
                                    <div className="action col">
                                        {item.application !== ALL_APPS && item.application !== ALL_AI_APPS && !UNIDENTIFIED_APPS.has(item.application.toLowerCase()) &&
                                            <ActionButton item={item} direction={direction} />}
                                    </div>
                            }]}
                        onRowClick={item => setAppName(item.application)}
                        isActiveRow={item => item.application === appName}
                    />}
            </div>
            {promiseContextHolder}
            {dependenciesWarningModalData &&
                <DependenciesWarningModal open={dependenciesWarningModalData.open}
                    setOpen={(open: boolean) => setDependenciesWarningModalData({
                        ...dependenciesWarningModalData,
                        open
                    })}
                    appName={dependenciesWarningModalData.appName}
                    dependentApps={dependenciesWarningModalData.dependentApps}
                    okCallback={dependenciesWarningModalData?.okCallback}
                    action={dependenciesWarningModalData.action} />
            }
        </div>
    );
});